import {useMemo} from 'react'
import {useAuth} from '@/contexts/auth-context'
import {OWNED_BY_ME_TRUE} from '@/lib/owned-by-me'
import type {EndpointContractFilterDescriptor, SearchAndFilterContract} from '@/types/endpoint-contract'

/**
 * Endpoint contract that carries an {@code ownedByMe} boolean filter under the
 * {@code OWNED_BY_ME} key — the shape every catalog list page consumed by a
 * MyResponsibilities tab conforms to.
 */
export interface OwnedByMeContract extends SearchAndFilterContract {
    readonly filter: Readonly<Record<string, EndpointContractFilterDescriptor>> & {
        readonly OWNED_BY_ME: EndpointContractFilterDescriptor
    }
}

export interface UseEmbeddedListPropsArgs {
    /** Endpoint contract — must declare an {@code OWNED_BY_ME} boolean filter. */
    readonly contract: OwnedByMeContract
    /**
     * When true, narrow the query to entities the caller owns and hide the
     * Create button — the MyResponsibilities tab shape.
     */
    readonly ownedByMe: boolean | undefined
    /**
     * Serialized search/filter/page state from {@code useServerPagination}, e.g.
     * `queryString` — Object.fromEntries(new URLSearchParams(baseQueryString))
     * yields the list-query param record.
     */
    readonly baseQueryString: string
    /**
     * Override the empty-state message rendered inside the list when there are
     * no rows — typically used in embedded mode to surface ownership-context
     * copy (e.g. "You are not listed as an owner of any access profile").
     * Does NOT affect the page heading.
     */
    readonly emptyStateTitle: string | undefined
    /** Page heading — used regardless of result-set size. */
    readonly defaultTitle: string
    /**
     * Permission string the caller needs to create a new entity — when the
     * permission is absent or {@code ownedByMe} is true, {@code canCreate}
     * resolves to false.
     */
    readonly createPermission: string
}

export interface UseEmbeddedListPropsResult {
    readonly listParams: Record<string, string>
    readonly canCreate: boolean
    /** Page heading — always the {@code defaultTitle}. */
    readonly title: string
    /**
     * Empty-state message override — the caller's {@code emptyStateTitle} when
     * present, otherwise {@code undefined} (so the page falls back to its own
     * default empty-state copy on the {@link TableEmptyState} component).
     */
    readonly emptyMessage: string | undefined
}

/**
 * Collapse the ~12-line "list params + canCreate + title" block that every
 * catalog list page (AccessProfiles, AutoAssignRules, CertificationCampaigns,
 * CertificationPolicies, FunctionGroups, Resources, RestrictionRules, Systems)
 * carried as duplication.
 *
 * <p>The hook builds the param record from the serialized pagination state,
 * appends the {@code OWNED_BY_ME=true} marker when {@code ownedByMe} is on,
 * folds the create-permission + ownedByMe gate into a single {@code canCreate}
 * flag, and resolves the title from the {@code emptyStateTitle} override or
 * the page default. The {@code embedded} prop stays a direct PageLayout prop
 * — it is a render-time concern, not a query concern.
 */
export function useEmbeddedListProps({
    contract,
    ownedByMe,
    baseQueryString,
    emptyStateTitle,
    defaultTitle,
    createPermission,
}: UseEmbeddedListPropsArgs): UseEmbeddedListPropsResult {
    const {hasPermission} = useAuth()
    const listParams = useMemo(() => {
        const params = Object.fromEntries(new URLSearchParams(baseQueryString)) as Record<string, string>
        if (ownedByMe) params[contract.filter.OWNED_BY_ME.name] = OWNED_BY_ME_TRUE
        return params
    }, [baseQueryString, ownedByMe, contract])
    const canCreate = hasPermission(createPermission) && !ownedByMe
    return {listParams, canCreate, title: defaultTitle, emptyMessage: emptyStateTitle}
}
